import { passwordSchema } from '@authsemply/shared';

/**
 * Robustesse d'un mot de passe, pour la jauge des formulaires (12/10/2026).
 *
 * Une INDICATION pour aider à choisir, pas une règle : la règle reste
 * `passwordSchema` (12 caractères, minuscule, majuscule, chiffre), appliquée
 * ici comme par le serveur. Un mot de passe qui ne la respecte pas ne dépasse
 * jamais « faible », quelle que soit sa longueur.
 *
 * Volontairement sans dépendance (zxcvbn pèse 400 ko pour un écran sur
 * dix) : longueur, variété des caractères, et une pénalité pour les motifs
 * qu'un attaquant essaie en premier.
 */

export type NiveauRobustesse = 0 | 1 | 2 | 3 | 4;

export interface Robustesse {
  niveau: NiveauRobustesse;
  libelle: string;
  /** Les quatre règles du portail, cochées ou non, dans l'ordre affiché. */
  criteres: { libelle: string; ok: boolean }[];
  conforme: boolean;
}

const LIBELLES: Record<NiveauRobustesse, string> = {
  0: 'Très faible',
  1: 'Faible',
  2: 'Moyen',
  3: 'Bon',
  4: 'Excellent',
};

/** Motifs essayés en premier : suites, claviers, mots évidents. */
const MOTIFS_FAIBLES = [
  /0123|1234|2345|3456|4567|5678|6789|7890/,
  /abcd|bcde|cdef|azer|qwer|wxcv|asdf/i,
  /mot ?de ?passe|password|motdepasse|semply|admin|bonjour|soleil/i,
  /(.)\1{2,}/, // trois fois le même caractère d'affilée
];

export function evaluerRobustesse(motDePasse: string): Robustesse {
  const criteres = [
    { libelle: '12 caractères', ok: motDePasse.length >= 12 },
    { libelle: 'une minuscule', ok: /[a-z]/.test(motDePasse) },
    { libelle: 'une majuscule', ok: /[A-Z]/.test(motDePasse) },
    { libelle: 'un chiffre', ok: /[0-9]/.test(motDePasse) },
  ];
  const conforme = passwordSchema.safeParse(motDePasse).success;

  if (!motDePasse) return { niveau: 0, libelle: LIBELLES[0], criteres, conforme: false };

  const familles = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter((r) => r.test(motDePasse)).length;
  let points = 0;
  if (motDePasse.length >= 12) points += 1;
  if (motDePasse.length >= 16) points += 1;
  if (motDePasse.length >= 20) points += 1;
  if (familles >= 3) points += 1;
  if (familles === 4) points += 1;
  points -= MOTIFS_FAIBLES.filter((r) => r.test(motDePasse)).length;

  let niveau = Math.max(0, Math.min(4, points - 1)) as NiveauRobustesse;
  // Hors règle : jamais au-dessus de « faible ».
  if (!conforme) niveau = Math.min(niveau, 1) as NiveauRobustesse;
  // Conforme : au moins « moyen » — la règle du portail est déjà honnête.
  else niveau = Math.max(niveau, 2) as NiveauRobustesse;

  return { niveau, libelle: LIBELLES[niveau], criteres, conforme };
}
